import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Building2, MapPin, User, Wrench, Droplets, Ruler, Shield, Save, ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { toTitleCase } from '@/lib/capitalize';
import WellLocationPicker from '@/components/common/WellLocationPicker';

const WMDS = [
  { value: 'swfwmd', label: 'Southwest Florida WMD' },
  { value: 'nwfwmd', label: 'Northwest Florida WMD' },
  { value: 'sjrwmd', label: 'St. Johns River WMD' },
  { value: 'sfwmd', label: 'South Florida WMD' },
  { value: 'srwmd', label: 'Suwannee River WMD' },
  { value: 'dep', label: 'DEP' },
];

const INTENDED_USES = [
  'domestic', 'bottled_water_supply',
  'public_water_supply_limited', 'public_water_supply_community',
  'agricultural_irrigation', 'landscape_irrigation',
  'commercial_industrial', 'livestock', 'nursery_irrigation',
  'golf_course_irrigation', 'recreation_area_irrigation',
  'monitoring', 'test', 'site_investigation',
  'earth_coupled_geothermal', 'hvac_supply', 'hvac_return',
  'class_i_injection',
  'class_v_recharge', 'class_v_recovery', 'class_v_air_sparge',
  'class_v_commercial_disposal', 'class_v_aquifer_storage', 'class_v_drainage',
  'remediation', 'other'
];

const INTENDED_USE_LABELS = {
  domestic: 'Domestic',
  bottled_water_supply: 'Bottled Water Supply',
  public_water_supply_limited: 'Public Water Supply (Limited Use/DOH)',
  public_water_supply_community: 'Public Water Supply (Community or Non-Community/DEP)',
  agricultural_irrigation: 'Agricultural Irrigation',
  landscape_irrigation: 'Landscape Irrigation',
  commercial_industrial: 'Commercial/Industrial',
  livestock: 'Livestock',
  nursery_irrigation: 'Nursery Irrigation',
  golf_course_irrigation: 'Golf Course Irrigation',
  recreation_area_irrigation: 'Recreation Area Irrigation',
  monitoring: 'Monitoring',
  test: 'Test',
  site_investigation: 'Site Investigation',
  earth_coupled_geothermal: 'Earth-Coupled Geothermal',
  hvac_supply: 'HVAC Supply',
  hvac_return: 'HVAC Return',
  class_i_injection: 'Class I Injection',
  class_v_recharge: 'Class V Injection: Recharge',
  class_v_recovery: 'Class V Injection: Recovery',
  class_v_air_sparge: 'Class V Injection: Air Sparge',
  class_v_commercial_disposal: 'Class V: Commercial/Industrial Disposal',
  class_v_aquifer_storage: 'Class V: Aquifer Storage and Recovery',
  class_v_drainage: 'Class V: Drainage',
  remediation: 'Remediation',
  other: 'Other (Describe)',
};

const WORK_TYPES = ['construction', 'repair', 'modification', 'abandonment'];

const CASING_MATERIALS = [
  'pvc', 'black_steel', 'galvanized', 'stainless_steel', 'not_cased', 'other'
];
const CASING_MATERIAL_LABELS = {
  pvc: 'PVC', black_steel: 'Black Steel', galvanized: 'Galvanized',
  stainless_steel: 'Stainless Steel', not_cased: 'Not Cased', other: 'Other',
};

const CONSTRUCTION_METHODS = [
  'rotary', 'cable_tool', 'auger', 'jetted', 'sonic',
  'combination', 'horizontal_drilling', 'hydraulic_point',
  'hand_driven', 'plugged_approved', 'other'
];
const CONSTRUCTION_METHOD_LABELS = {
  rotary: 'Rotary', cable_tool: 'Cable Tool', auger: 'Auger',
  jetted: 'Jetted', sonic: 'Sonic',
  combination: 'Combination (Two or More Methods)',
  horizontal_drilling: 'Horizontal Drilling',
  hydraulic_point: 'Hydraulic Point (Direct Push)',
  hand_driven: 'Hand Driven (Well Point, Sand Point)',
  plugged_approved: 'Plugged by Approved Method',
  other: 'Other',
};

const SEAL_MATERIALS = ['bentonite', 'neat_cement', 'sand_cement', 'other'];
const DATUMS = ['nad_27', 'nad_83', 'wgs_84'];
const DATA_SOURCES = ['gps', 'map', 'survey'];

const COMPANY_INFO = {
  name: 'Blue Well LLC',
  address: '3307 Baxter Ave',
  city: 'Sebring',
  state: 'FL',
  zip: '33870',
  phone: '(863) 658-0035',
  email: 'bluewell.llc@gmail.com',
  license: '9566',
};

const emptyGroutRow = () => ({ from: '', to: '', seal_material: 'bentonite' });

function Section({ title, icon: Icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors">
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          {Icon && <Icon className="w-4 h-4 text-[#003366]" />} {title}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="p-4 space-y-3">{children}</div>}
    </div>
  );
}

export default function WellApplicationForm({ permit, project, contact, estimate, onSave, onClose }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    wmd: 'swfwmd',
    delegated_authority: '',
    permit_number: '', florida_unique_id: '',
    owner_name: '', owner_address: '', owner_city: '', owner_state: 'FL', owner_zip: '', owner_phone: '', owner_email: '',
    well_address: '', well_city: '', well_state: 'FL', well_zip: '', well_county: '',
    section: '', township: '', range_field: '', subdivision: '',
    parcel_id: '', lot: '', block: '', unit: '',
    check_62_524: 'no',
    contractor_name: COMPANY_INFO.name, contractor_address: COMPANY_INFO.address,
    contractor_city: COMPANY_INFO.city, contractor_state: COMPANY_INFO.state,
    contractor_zip: COMPANY_INFO.zip, contractor_phone: COMPANY_INFO.phone,
    contractor_email: COMPANY_INFO.email, contractor_license: COMPANY_INFO.license,
    driller_name: '',
    work_type: 'construction', repair_reason: '',
    intended_uses: [], other_use_description: '',
    num_wells: '1', septic_distance: '', facility_description: '', start_date: '',
    well_depth: '', casing_depth: '', casing_diameter: '',
    open_hole_from: '', open_hole_to: '',
    screen_from: '', screen_to: '',
    casing_material: 'pvc', construction_method: 'rotary',
    secondary_casing_type: '', secondary_casing_diameter: '',
    secondary_casing_material: '',
    grouting_rows: [emptyGroutRow()],
    existing_wells: '0', unused_wells: '0',
    has_cup: 'no', cup_number: '', district_well_id: '',
    latitude: '', longitude: '', data_source: 'gps', datum: 'nad_83',
    contractor_signature_date: '', owner_signature_date: '',
    additional_notes: '',
  });

  useEffect(() => {
    const updates = {};
    if (permit?.well_application && Object.keys(permit.well_application).length > 0) {
      Object.assign(updates, permit.well_application);
      if (updates.grouting_rows && !Array.isArray(updates.grouting_rows)) {
        updates.grouting_rows = [emptyGroutRow()];
      }
    } else {
      // Auto-populate from contact (owner info)
      if (contact) {
        updates.owner_name = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
        updates.owner_phone = contact.phone || '';
        updates.owner_email = contact.email || '';
      }

      // Auto-populate owner mailing address from estimate.mailing_address or contact
      if (estimate?.mailing_address?.address) {
        updates.owner_address = estimate.mailing_address.address;
        updates.owner_city = estimate.mailing_address.city || '';
        updates.owner_state = estimate.mailing_address.state || 'FL';
        updates.owner_zip = estimate.mailing_address.zip || '';
      } else if (contact) {
        updates.owner_address = contact.address || '';
        updates.owner_city = contact.city || '';
        updates.owner_state = contact.state || 'FL';
        updates.owner_zip = contact.zip || '';
      }

      // Auto-populate from estimate property_info (Register Owner, Parcel ID, County)
      if (estimate?.property_info) {
        const pi = estimate.property_info;
        if (pi.owner_name) updates.owner_name = pi.owner_name;
        if (pi.parcel_id) updates.parcel_id = pi.parcel_id;
        if (pi.county) updates.well_county = pi.county;
      }

      // Auto-populate well/job site address from estimate
      if (estimate?.well_address) {
        updates.well_address = estimate.well_address;
      }
      if (estimate?.well_coordinates) {
        if (estimate.well_coordinates.lat) updates.latitude = estimate.well_coordinates.lat;
        if (estimate.well_coordinates.lng) updates.longitude = estimate.well_coordinates.lng;
      }

      // Auto-populate from project location (fallback)
      if (project) {
        if (!updates.well_address) updates.well_address = project.location?.address || permit?.property_address || '';
        updates.well_city = updates.well_city || project.location?.city || '';
        if (!updates.well_county) updates.well_county = project.location?.county || permit?.county || '';
        if (!updates.parcel_id) updates.parcel_id = project.location?.parcel_id || permit?.parcel_id || '';
        if (!updates.latitude) updates.latitude = project.location?.latitude || permit?.latitude || '';
        if (!updates.longitude) updates.longitude = project.location?.longitude || permit?.longitude || '';
        updates.township = updates.township || project.location?.township || permit?.township || '';
        updates.range_field = updates.range_field || project.location?.range || permit?.range || '';
        updates.section = updates.section || project.location?.section || permit?.section || '';
      }

      // Auto-populate well technical info from estimate
      if (estimate?.well_info) {
        const wi = estimate.well_info;
        updates.well_depth = wi.well_depth || '';
      // Auto-populate casing from estimate well_info
      if (wi.casing_sections && wi.casing_sections.length > 0) {
        // Telescopic: use deepest section's to_depth as casing depth, first section's diameter/material
        const deepest = wi.casing_sections[wi.casing_sections.length - 1];
        updates.casing_depth = deepest.to_depth || '';
        updates.casing_diameter = wi.casing_sections[0].diameter || '';
        updates.casing_material = wi.casing_sections[0].material || 'pvc';
      } else {
        updates.casing_depth = wi.casing_to || wi.casing_depth || '';
        updates.casing_diameter = wi.casing_diameter || '';
        updates.casing_material = wi.casing_material || wi.casing_type || 'pvc';
      }
        updates.construction_method = wi.construction_method || 'rotary';
        updates.septic_distance = wi.septic_distance || '';
        if (wi.screen_from) updates.screen_from = wi.screen_from;
        if (wi.screen_to) updates.screen_to = wi.screen_to;
        // Handle NEW completion_intervals format (overrides old format if present)
        if (wi.completion_intervals && wi.completion_intervals.length > 0) {
          const screenInterval = wi.completion_intervals.find(i => i.type === 'screen');
          const openHoleInterval = wi.completion_intervals.find(i => i.type === 'open_hole');
          if (screenInterval) {
            updates.screen_from = screenInterval.from_depth?.toString() || '';
            updates.screen_to = screenInterval.to_depth?.toString() || '';
          }
          if (openHoleInterval) {
            updates.open_hole_from = openHoleInterval.from_depth?.toString() || '';
            updates.open_hole_to = openHoleInterval.to_depth?.toString() || '';
          }
        }
        // Map well_use to intended_uses with proper value mapping
        if (wi.well_use) {
          const useMap = { irrigation: 'agricultural_irrigation', commercial: 'commercial_industrial', industrial: 'commercial_industrial' };
          updates.intended_uses = [useMap[wi.well_use] || wi.well_use];
        }
        // Map work_type from estimate (new_well → construction for permit)
        if (wi.work_type) {
          const workTypeMap = { new_well: 'construction', new_construction: 'construction' };
          updates.work_type = workTypeMap[wi.work_type] || wi.work_type;
        }
        if (wi.abandonment_reason && wi.work_type === 'abandonment') {
          updates.repair_reason = wi.abandonment_reason;
        }
      }

      // Auto-populate from permit data (fallback)
      if (permit) {
        updates.work_type = updates.work_type || permit.type || 'construction';
        updates.permit_number = permit.permit_number || permit.permit_number_auto || '';
        if (!updates.well_county) updates.well_county = permit.county || '';
        if (!updates.latitude) updates.latitude = permit.latitude || '';
        if (!updates.longitude) updates.longitude = permit.longitude || '';
        if (!updates.parcel_id) updates.parcel_id = permit.parcel_id || '';
        if (!updates.township) updates.township = permit.township || '';
        if (!updates.range_field) updates.range_field = permit.range || '';
        if (!updates.section) updates.section = permit.section || '';
        if (!updates.owner_name) updates.owner_name = permit.property_owner || '';
        if (!updates.well_address) updates.well_address = permit.property_address || '';
      }

      // Auto-set today's date for contractor signature date
      if (!updates.contractor_signature_date) {
        updates.contractor_signature_date = new Date().toISOString().slice(0, 10);
      }
    }
    setForm(prev => ({ ...prev, ...updates }));
  }, [permit, project, contact, estimate]);

  const toggleUse = (use) => {
    setForm(prev => ({
      ...prev,
      intended_uses: prev.intended_uses.includes(use)
        ? prev.intended_uses.filter(u => u !== use)
        : [...prev.intended_uses, use]
    }));
  };

  const handleSave = () => { if (onSave) onSave(form); };
  const u = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  const addGroutRow = () => setForm(prev => ({ ...prev, grouting_rows: [...prev.grouting_rows, emptyGroutRow()] }));
  const removeGroutRow = (idx) => setForm(prev => ({ ...prev, grouting_rows: prev.grouting_rows.filter((_, i) => i !== idx) }));
  const updateGroutRow = (idx, field, val) => {
    setForm(prev => {
      const rows = [...prev.grouting_rows];
      rows[idx] = { ...rows[idx], [field]: val };
      return { ...prev, grouting_rows: rows };
    });
  };

  return (
    <div className="space-y-4" data-testid="well-application-form">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#003366]" /> Statewide Well Application
        </h2>
      </div>

      <Section title="1. Water Management District" icon={Building2}>
        <Select value={form.wmd} onValueChange={v => u('wmd', v)}>
          <SelectTrigger data-testid="wa-wmd"><SelectValue /></SelectTrigger>
          <SelectContent>
            {WMDS.map(w => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="space-y-1 mt-2">
          <Label className="text-xs">Delegated Authority (If Applicable)</Label>
          <Input value={form.delegated_authority} onChange={e => u('delegated_authority', e.target.value)} placeholder="Delegated authority name" />
        </div>
      </Section>

      <Section title="Permit Info (Official Use)" icon={FileText} defaultOpen={false}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Permit No.</Label><Input value={form.permit_number} onChange={e => u('permit_number', e.target.value)} data-testid="wa-permit-num" /></div>
          <div className="space-y-1"><Label className="text-xs">Florida Unique ID</Label><Input value={form.florida_unique_id} onChange={e => u('florida_unique_id', e.target.value)} /></div>
        </div>
      </Section>

      <Section title="2. Owner Information" icon={User}>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1">
            <Label className="text-xs">Owner Name *</Label>
            <Input value={form.owner_name} onChange={e => u('owner_name', toTitleCase(e.target.value))} placeholder="Property owner name" data-testid="wa-owner-name" />
          </div>
          <div className="col-span-2 space-y-1">
            <Label className="text-xs">Address</Label>
            <Input value={form.owner_address} onChange={e => u('owner_address', toTitleCase(e.target.value))} placeholder="Street address" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">City</Label>
            <Input value={form.owner_city} onChange={e => u('owner_city', toTitleCase(e.target.value))} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">State</Label>
              <Input value={form.owner_state} onChange={e => u('owner_state', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">ZIP</Label>
              <Input value={form.owner_zip} onChange={e => u('owner_zip', e.target.value)} />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Phone</Label>
            <Input value={form.owner_phone} onChange={e => u('owner_phone', e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Email</Label>
            <Input type="email" value={form.owner_email} onChange={e => u('owner_email', e.target.value)} placeholder="owner@email.com" data-testid="wa-owner-email" />
          </div>
        </div>
      </Section>

      <Section title="3. Well Location" icon={MapPin}>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1">
            <Label className="text-xs">Address *</Label>
            <Input value={form.well_address} onChange={e => u('well_address', toTitleCase(e.target.value))} data-testid="wa-well-address" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">City</Label>
            <Input value={form.well_city} onChange={e => u('well_city', toTitleCase(e.target.value))} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">State</Label>
            <Input value={form.well_state} onChange={e => u('well_state', e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">ZIP</Label>
            <Input value={form.well_zip} onChange={e => u('well_zip', e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">County *</Label>
            <Input value={form.well_county} onChange={e => u('well_county', e.target.value)} data-testid="wa-county" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Section *</Label>
            <Input value={form.section} onChange={e => u('section', e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Township *</Label>
            <Input value={form.township} onChange={e => u('township', e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Range *</Label>
            <Input value={form.range_field} onChange={e => u('range_field', e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Subdivision</Label>
            <Input value={form.subdivision} onChange={e => u('subdivision', e.target.value)} />
          </div>
        </div>
      </Section>

      {/* Well Location Map */}
      {(form.latitude && form.longitude && parseFloat(form.latitude) !== 0 && parseFloat(form.longitude) !== 0) && (
        <div className="border border-indigo-200 rounded-lg overflow-hidden bg-indigo-50/30 p-3">
          <WellLocationPicker
            coordinates={{ lat: parseFloat(form.latitude), lng: parseFloat(form.longitude) }}
            wellMarker={estimate?.well_info?.well_marker || null}
            address={form.well_address}
            readOnly={false}
            onSave={(marker) => {
              u('well_marker', marker);
            }}
          />
        </div>
      )}

      <Section title="4. Parcel ID" icon={MapPin} defaultOpen={false}>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1">
            <Label className="text-xs">PIN / Alternate Key</Label>
            <Input value={form.parcel_id} onChange={e => u('parcel_id', e.target.value)} data-testid="wa-parcel" />
          </div>
          <div className="space-y-1"><Label className="text-xs">Lot</Label><Input value={form.lot} onChange={e => u('lot', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Block</Label><Input value={form.block} onChange={e => u('block', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Unit</Label><Input value={form.unit} onChange={e => u('unit', e.target.value)} /></div>
          <div className="space-y-1">
            <Label className="text-xs">Check if 62-524</Label>
            <Select value={form.check_62_524} onValueChange={v => u('check_62_524', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Section>

      <Section title="5. Contractor Information" icon={Shield}>
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-2">
          <p className="text-xs text-blue-600 font-medium">Auto-filled from company settings</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Company Name</Label><Input value={form.contractor_name} onChange={e => u('contractor_name', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">License #</Label><Input value={form.contractor_license} onChange={e => u('contractor_license', e.target.value)} /></div>
          <div className="col-span-2 space-y-1"><Label className="text-xs">Address</Label><Input value={form.contractor_address} onChange={e => u('contractor_address', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">City</Label><Input value={form.contractor_city} onChange={e => u('contractor_city', e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1"><Label className="text-xs">State</Label><Input value={form.contractor_state} onChange={e => u('contractor_state', e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">ZIP</Label><Input value={form.contractor_zip} onChange={e => u('contractor_zip', e.target.value)} /></div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Phone</Label><Input value={form.contractor_phone} onChange={e => u('contractor_phone', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Email</Label><Input value={form.contractor_email} onChange={e => u('contractor_email', e.target.value)} /></div>
          <div className="col-span-2 space-y-1"><Label className="text-xs">Water Well Contractor (Driller Name)</Label><Input value={form.driller_name} onChange={e => u('driller_name', e.target.value)} placeholder="Driller's name" /></div>
        </div>
      </Section>

      <Section title="6. Type of Work *" icon={Wrench}>
        <div className="flex flex-wrap gap-2">
          {WORK_TYPES.map(wt => (
            <button key={wt} type="button" onClick={() => u('work_type', wt)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${form.work_type === wt ? 'bg-[#003366] text-white border-[#003366]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
              data-testid={`wa-work-${wt}`}>
              {t(`permits.types.${wt}`, wt.charAt(0).toUpperCase() + wt.slice(1))}
            </button>
          ))}
        </div>
        {(form.work_type === 'repair' || form.work_type === 'modification' || form.work_type === 'abandonment') && (
          <div className="space-y-1 mt-2">
            <Label className="text-xs">Reason for {form.work_type} *</Label>
            <Textarea value={form.repair_reason} onChange={e => u('repair_reason', e.target.value)} rows={2} placeholder="Reason for repair/modification/abandonment..." />
          </div>
        )}
      </Section>

      <Section title="7. Number of Proposed Wells" icon={Droplets}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Number of Proposed Wells *</Label><Input type="number" value={form.num_wells} onChange={e => u('num_wells', e.target.value)} min="1" /></div>
        </div>
      </Section>

      <Section title="8. Intended Use(s) of Well(s) *" icon={Droplets}>
        <div className="grid grid-cols-2 gap-1.5">
          {INTENDED_USES.map(use => (
            <button key={use} type="button" onClick={() => toggleUse(use)}
              className={`text-left px-2.5 py-1.5 rounded text-xs border transition-all ${form.intended_uses.includes(use) ? 'bg-[#003366]/10 text-[#003366] border-[#003366]/30 font-medium' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`}
              data-testid={`wa-use-${use}`}>
              {INTENDED_USE_LABELS[use] || use}
            </button>
          ))}
        </div>
        {form.intended_uses.includes('other') && (
          <div className="space-y-1 mt-2">
            <Label className="text-xs">Other - Describe</Label>
            <Input value={form.other_use_description} onChange={e => u('other_use_description', e.target.value)} placeholder="Describe other use..." />
          </div>
        )}
      </Section>

      <Section title="9. Distance from Septic System" icon={Ruler}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Distance from Septic (ft) * (if &le; 200 ft)</Label><Input value={form.septic_distance} onChange={e => u('septic_distance', e.target.value)} data-testid="wa-septic" /></div>
        </div>
      </Section>

      <Section title="10. Facility Description" icon={FileText} defaultOpen={false}>
        <div className="space-y-1">
          <Label className="text-xs">Facility Description</Label>
          <Textarea value={form.facility_description} onChange={e => u('facility_description', e.target.value)} rows={2} placeholder="Describe the facility..." data-testid="wa-facility-desc" />
        </div>
      </Section>

      <Section title="11. Estimated Start Date" icon={FileText}>
        <div className="grid grid-cols-4 gap-3">
          <div className="space-y-1 col-span-2"><Label className="text-xs">Estimated Start Date</Label><Input type="date" value={form.start_date} onChange={e => u('start_date', e.target.value)} className="h-8 text-xs" /></div>
        </div>
      </Section>

      <Section title="12. Well Depth & Casing *" icon={Ruler}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Est. Well Depth (ft) *</Label><Input value={form.well_depth} onChange={e => u('well_depth', e.target.value)} data-testid="wa-depth" /></div>
          <div className="space-y-1"><Label className="text-xs">Est. Casing Depth (ft) *</Label><Input value={form.casing_depth} onChange={e => u('casing_depth', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Primary Casing Diameter (in) *</Label><Input value={form.casing_diameter} onChange={e => u('casing_diameter', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Open Hole: From (ft)</Label><Input value={form.open_hole_from} onChange={e => u('open_hole_from', e.target.value)} data-testid="wa-open-hole-from" /></div>
          <div className="space-y-1"><Label className="text-xs">Open Hole: To (ft)</Label><Input value={form.open_hole_to} onChange={e => u('open_hole_to', e.target.value)} data-testid="wa-open-hole-to" /></div>
        </div>
      </Section>

      <Section title="13. Screen Interval" icon={Ruler}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Screen From (ft)</Label><Input value={form.screen_from} onChange={e => u('screen_from', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Screen To (ft)</Label><Input value={form.screen_to} onChange={e => u('screen_to', e.target.value)} /></div>
        </div>
      </Section>

      <Section title="14. Primary Casing Material *" icon={Wrench}>
        <div className="flex flex-wrap gap-2">
          {CASING_MATERIALS.map(m => (
            <button key={m} type="button" onClick={() => u('casing_material', m)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${form.casing_material === m ? 'bg-[#003366] text-white border-[#003366]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
              {CASING_MATERIAL_LABELS[m]}
            </button>
          ))}
        </div>
      </Section>

      <Section title="15. Secondary Casing" icon={Wrench} defaultOpen={false}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Type</Label>
            <Select value={form.secondary_casing_type || 'none'} onValueChange={v => u('secondary_casing_type', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="telescope">Telescope Casing</SelectItem>
                <SelectItem value="liner">Liner</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Surface Casing Diameter (in)</Label><Input value={form.secondary_casing_diameter} onChange={e => u('secondary_casing_diameter', e.target.value)} /></div>
          <div className="space-y-1">
            <Label className="text-xs">Secondary Casing Material</Label>
            <Select value={form.secondary_casing_material || 'none'} onValueChange={v => u('secondary_casing_material', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {CASING_MATERIALS.filter(m => m !== 'not_cased').map(m => (
                  <SelectItem key={m} value={m}>{CASING_MATERIAL_LABELS[m]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Section>

      <Section title="16. Method of Construction *" icon={Wrench}>
        <div className="flex flex-wrap gap-2">
          {CONSTRUCTION_METHODS.map(m => (
            <button key={m} type="button" onClick={() => u('construction_method', m)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${form.construction_method === m ? 'bg-[#003366] text-white border-[#003366]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
              {CONSTRUCTION_METHOD_LABELS[m]}
            </button>
          ))}
        </div>
      </Section>

      <Section title="17. Grouting Intervals" icon={Wrench} defaultOpen={false}>
        <p className="text-xs text-gray-500 mb-2">Proposed grouting interval for primary, secondary, and additional casing</p>
        <div className="space-y-2">
          <div className="grid grid-cols-7 gap-1 text-[10px] font-semibold text-gray-500 uppercase px-1">
            <span className="col-span-2">From (ft)</span>
            <span className="col-span-2">To (ft)</span>
            <span className="col-span-2">Seal Material</span>
            <span className="col-span-1"></span>
          </div>
          {form.grouting_rows.map((row, idx) => (
            <div key={idx} className="grid grid-cols-7 gap-1 items-center" data-testid={`wa-grout-${idx}`}>
              <Input className="col-span-2 h-8 text-xs" value={row.from} onChange={e => updateGroutRow(idx, 'from', e.target.value)} placeholder="0" />
              <Input className="col-span-2 h-8 text-xs" value={row.to} onChange={e => updateGroutRow(idx, 'to', e.target.value)} placeholder="0" />
              <Select value={row.seal_material} onValueChange={v => updateGroutRow(idx, 'seal_material', v)}>
                <SelectTrigger className="col-span-2 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{SEAL_MATERIALS.map(m => <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1).replace('_', ' ')}</SelectItem>)}</SelectContent>
              </Select>
              <Button type="button" variant="ghost" size="icon" className="col-span-1 h-8 w-8 text-red-400" onClick={() => removeGroutRow(idx)} disabled={form.grouting_rows.length <= 1}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ))}
          {form.grouting_rows.length < 4 && (
            <Button type="button" variant="outline" size="sm" onClick={addGroutRow} className="w-full">
              <Plus className="w-3 h-3 mr-1" /> Add Row
            </Button>
          )}
        </div>
      </Section>

      <Section title="18. Existing Wells on Site" icon={Droplets} defaultOpen={false}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Total Existing Wells on Site</Label><Input type="number" value={form.existing_wells} onChange={e => u('existing_wells', e.target.value)} min="0" /></div>
          <div className="space-y-1"><Label className="text-xs">Unused Wells</Label><Input type="number" value={form.unused_wells} onChange={e => u('unused_wells', e.target.value)} min="0" /></div>
        </div>
      </Section>

      <Section title="19. CUP/WUP *" icon={FileText} defaultOpen={false}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Covered under CUP/WUP?</Label>
            <Select value={form.has_cup} onValueChange={v => u('has_cup', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.has_cup === 'yes' && (
            <>
              <div className="space-y-1"><Label className="text-xs">CUP/WUP Number</Label><Input value={form.cup_number} onChange={e => u('cup_number', e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-xs">District Well ID No.</Label><Input value={form.district_well_id} onChange={e => u('district_well_id', e.target.value)} /></div>
            </>
          )}
        </div>
      </Section>

      <Section title="20. Coordinates" icon={MapPin}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Latitude</Label><Input value={form.latitude} onChange={e => u('latitude', e.target.value)} data-testid="wa-lat" /></div>
          <div className="space-y-1"><Label className="text-xs">Longitude</Label><Input value={form.longitude} onChange={e => u('longitude', e.target.value)} data-testid="wa-lng" /></div>
          <div className="space-y-1">
            <Label className="text-xs">Data Obtained From</Label>
            <Select value={form.data_source} onValueChange={v => u('data_source', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {DATA_SOURCES.map(s => <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Datum</Label>
            <Select value={form.datum} onValueChange={v => u('datum', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {DATUMS.map(d => <SelectItem key={d} value={d}>{d.toUpperCase().replace('_', ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Section>

      <Section title="21. Signatures & Dates" icon={FileText} defaultOpen={false}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Contractor Signature Date</Label><Input type="date" value={form.contractor_signature_date} onChange={e => u('contractor_signature_date', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Owner/Agent Signature Date</Label><Input type="date" value={form.owner_signature_date} onChange={e => u('owner_signature_date', e.target.value)} /></div>
        </div>
      </Section>

      <div className="space-y-1">
        <Label className="text-xs">Additional Notes / Comments</Label>
        <Textarea value={form.additional_notes} onChange={e => u('additional_notes', e.target.value)} rows={2} placeholder="Additional notes or comments..." />
      </div>

      <div className="flex gap-2 justify-end pt-2 border-t">
        {onClose && <Button type="button" variant="outline" onClick={onClose}>Close</Button>}
        <Button type="button" onClick={handleSave} className="bg-[#003366] hover:bg-[#004080] text-white" data-testid="wa-save-btn">
          <Save className="w-4 h-4 mr-2" /> Save Well Application
        </Button>
      </div>
    </div>
  );
}
